import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, getOppositeLocale } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "../../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../../common/contexts/CurrentUserContext";
import { trpc } from "../../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { FormTypeDiscriminator } from "../../../common/types";

import { TranslationActivityProvider } from "./Context/TranslationActivityContext";
import { TranslationLocaleResources } from "./TranslationModalLayout/TranslationLocaleResources";

import { AutoHideDuration } from "@/common/constants/Notistack";
import { TicketingOutput, DonationFormWithUserOrOrgAccessOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import RemoveTranslationsDialogValidation from "@/components/RemoveTranslationsDialogValidation/RemoveTranslationsDialogValidation";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DonationTranslationModal } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/DonationTranslations/DonationTranslationModal";
import { TicketingTranslationModal } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/TicketingTranslations/TicketingTranslationModal";

interface AdvancedOptionsTranslationsCardProps extends FormTypeDiscriminator<FormType> {}

export const AdvancedOptionsTranslationsCard: FC<AdvancedOptionsTranslationsCardProps> = ({ formType }) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { organization } = useCurrentUserContext();
  const { apiFormObject, updateApiFormObject, formHasTranslation } = useLiveFormEditorContext();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const [hasTranslation, setHasTranslation] = useState<boolean | undefined>(formHasTranslation);
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);

  const { mutateAsync: removeTranslations, isPending: removeTranslationsLoading } =
    formType === FormType.DonationForm
      ? trpc.removeDonationFormTranslations.useMutation()
      : trpc.removeTicketingTranslations.useMutation();
  const utils = trpc.useUtils();

  const translationLocale = getOppositeLocale({
    organizationCountry: organization?.country,
    locale: apiFormObject.locale,
  });
  const translationLocaleLanguageWord = t("common", `locale.${translationLocale}`);

  const handleRemoveTranslations = async () => {
    const { data } = await removeTranslations({ id: apiFormObject.id, locale: translationLocale });

    if (data) {
      enqueueSnackbar(t("dashboard", "form.removeTranslationsDialogValidation.success"), {
        vibe: "positive",
        autoHideDuration: AutoHideDuration.fast,
      });

      if (formType === FormType.DonationForm) {
        updateApiFormObject(apiFormObject as Partial<DonationFormWithUserOrOrgAccessOutput>);
      } else {
        updateApiFormObject(apiFormObject as Partial<TicketingOutput>);
        await utils.getLiveFormRemindersData.invalidate({ ticketingId: apiFormObject.id });
      }
    } else {
      enqueueSnackbar(t("dashboard", "form.removeTranslationsDialogValidation.error"), {
        vibe: "danger",
        autoHideDuration: AutoHideDuration.fast,
      });
    }
  };

  return (
    <LiveEditorBlock
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", md: "center" }}
      columnGap={1}
      rowGap={1}
    >
      {/* Title and Description */}
      <Box>
        <DashboardTypography variant="body1">
          {t("dashboard", "translateTo.title", {
            locale: translationLocaleLanguageWord.charAt(0).toUpperCase() + translationLocaleLanguageWord.slice(1),
          })}
        </DashboardTypography>
        <DashboardTypography variant="body2">
          {t("dashboard", "translateTo.description", { locale: translationLocaleLanguageWord })}
        </DashboardTypography>
      </Box>

      {/* Buttons */}
      {!hasTranslation ? (
        <DashboardButton
          size="small"
          vibe="neutral"
          variant="tonal"
          onClick={() => {
            logUserActionEvent(AmplitudeEvents.FormTranslationStarted);
            setIsTranslationModalOpen(true);
          }}
          sx={{ whiteSpace: "nowrap" }}
        >
          {t("dashboard", "translateTo.button", {
            locale: translationLocaleLanguageWord.charAt(0).toUpperCase() + translationLocaleLanguageWord.slice(1),
          })}
        </DashboardButton>
      ) : (
        <Stack direction="row" spacing={1}>
          {/* Edit Translations Button */}
          <DashboardButton
            variant="outlined"
            vibe="warning"
            onClick={() => {
              logUserActionEvent(AmplitudeEvents.FormTranslationStarted);
              setIsTranslationModalOpen(true);
            }}
          >
            {t("dashboard", "translateTo.button.edit", { locale: translationLocaleLanguageWord })}
          </DashboardButton>
          {/* Remove Translations Button */}
          <RemoveTranslationsDialogValidation
            removeTranslations={handleRemoveTranslations}
            removeTranslationsLoading={removeTranslationsLoading}
            translationLocale={translationLocale}
            onRemoveSuccess={() => {
              setHasTranslation(false);
            }}
          />
        </Stack>
      )}

      {isTranslationModalOpen && (
        <TranslationActivityProvider>
          <TranslationLocaleResources
            formLocale={apiFormObject.locale}
            translationLocale={translationLocale}
            organizationCountry={organization?.country}
            onClose={() => setIsTranslationModalOpen(false)}
          >
            {formType === FormType.DonationForm ? (
              <DonationTranslationModal
                isOpen={isTranslationModalOpen}
                closeModal={() => setIsTranslationModalOpen(false)}
                onSave={() => {
                  setHasTranslation(true);
                }}
              />
            ) : (
              <TicketingTranslationModal
                isOpen={isTranslationModalOpen}
                closeModal={() => setIsTranslationModalOpen(false)}
                onSave={() => {
                  setHasTranslation(true);
                }}
              />
            )}
          </TranslationLocaleResources>
        </TranslationActivityProvider>
      )}
    </LiveEditorBlock>
  );
};
